import { API_BASE, request } from './http'
import type { PanelImageRenderRequest } from '@/lib/workbenchConfig'
import type { Asset } from '@/types/nf'

export interface StoryboardPanel {
  id: string
  storyboard_asset_id: string
  panel_sequence: number
  payload: string
  created_at: string
}

interface StoryboardPanelsResponse {
  panels: StoryboardPanel[] | null
}

export interface SketchFigure {
  character_id: string
  bbox?: [number, number, number, number]
  facing?: string
  slot?: string
  depth?: string
  gaze?: string
  posture?: string
}

export interface SketchFrame {
  panel_id: string
  shot_type: string
  frame_role?: string
  figures: SketchFigure[]
  spatial_anchor?: string
  status: 'pending' | 'confirmed' | 'modified' | string
  thumbnail_ref?: string
  vlm_result?: SketchVLMResult | null
  pool_candidates?: SketchPoolCandidate[]
}

export interface SketchPoolCandidate {
  asset_id: string
  image_ref: string
  score: number
  rank: number
  primary: boolean
}

export interface SketchFramesResponse {
  frames: SketchFrame[]
  vlm_result?: SketchVLMResult | null
}

export interface SketchFrameResponse {
  frame: SketchFrame
}

export interface UpdateSketchFrameRequest {
  figures: SketchFigure[]
}

export interface SketchVLMResult {
  assessment?: {
    status?: string
    score?: number
  }
  issues?: Array<{
    code?: string
    message?: string
    severity?: string
    blocking?: boolean
  }>
}

export interface PanelAnnotationSummary {
  shotType?: string
  composition?: string
  dialogueBubbles: string[]
  narrationBox?: string
}

export interface RegenerateJobResponse {
  job_id?: string
  status?: string
}

export interface VideoStationStatus {
  station_disabled: boolean
}

export interface ComposeVideoRequest {
  aspect?: '16:9' | '9:16'
  bgm?: string
}

export interface BGMTrack {
  name: string
}

interface BGMTracksResponse {
  items?: BGMTrack[] | null
}

export interface ComposeJobView {
  job_id: string
  project_id: string
  chapter_id: string
  branch_id: string
  aspect: '16:9' | '9:16' | string
  status: 'queued' | 'running' | 'done' | 'failed' | string
  progress?: string
  error?: string
  degraded?: string[]
  result?: {
    asset_id?: string
    video_key?: string
    degraded?: string[]
  }
}

export interface AudioVoiceOverride {
  narrator_voice?: string
  voice_map?: Record<string, string>
  speech_rate?: number
}

export interface ScreenplayDialogue {
  character?: string
  content?: string
  bubble_type?: string
}

export interface ScreenplayScene {
  scene_id?: string
  setting?: string
  action?: string
  narration?: string
  dialogues?: ScreenplayDialogue[]
}

export interface ScreenplayPayload {
  chapter_id?: string
  scenes?: ScreenplayScene[]
}

interface ScreenplayAssetPayload {
  screenplay?: ScreenplayPayload
  quality?: unknown
}

interface StoryboardAssetPayload {
  panels?: StoryboardPanel[]
  storyboard?: {
    panels?: StoryboardPanel[]
  }
  quality?: unknown
}

function parseJsonRecord(raw: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

export function parseScreenplayPayload(asset: Asset | null | undefined): ScreenplayPayload | null {
  if (!asset?.payload) return null
  const parsed = parseJsonRecord(asset.payload) as ScreenplayAssetPayload | null
  return parsed?.screenplay && typeof parsed.screenplay === 'object' ? parsed.screenplay : null
}

function isStoryboardPanel(value: unknown): value is StoryboardPanel {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return (
    typeof record.id === 'string' &&
    typeof record.storyboard_asset_id === 'string' &&
    typeof record.panel_sequence === 'number' &&
    typeof record.payload === 'string' &&
    typeof record.created_at === 'string'
  )
}

export function parseStoryboardPanels(asset: Asset | null | undefined): StoryboardPanel[] {
  if (!asset?.payload) return []
  const parsed = parseJsonRecord(asset.payload) as StoryboardAssetPayload | null
  const panels = Array.isArray(parsed?.panels) ? parsed.panels : Array.isArray(parsed?.storyboard?.panels) ? parsed.storyboard.panels : []
  return sortStoryboardPanels(panels.filter(isStoryboardPanel))
}

export function parsePanelPayload(panel: StoryboardPanel): Record<string, unknown> | null {
  return parseJsonRecord(panel.payload)
}

function stringField(record: Record<string, unknown> | null, field: string) {
  const value = record?.[field]
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

export function sortStoryboardPanels(panels: StoryboardPanel[]): StoryboardPanel[] {
  return [...panels].sort((a, b) => a.panel_sequence - b.panel_sequence || a.id.localeCompare(b.id))
}

export function panelAnnotationSummary(panel: StoryboardPanel): PanelAnnotationSummary {
  const payload = parsePanelPayload(panel)
  const dialogueBubbles = Array.isArray(payload?.dialogue_bubbles)
    ? payload.dialogue_bubbles
        .map((bubble) => {
          if (!bubble || typeof bubble !== 'object') return undefined
          const record = bubble as Record<string, unknown>
          const character = stringField(record, 'character')
          const content = stringField(record, 'content')
          if (!content) return undefined
          return character ? `${character}: ${content}` : content
        })
        .filter((bubble): bubble is string => Boolean(bubble))
    : []
  return {
    shotType: stringField(payload, 'shot_type'),
    composition: stringField(payload, 'composition'),
    dialogueBubbles,
    narrationBox: stringField(payload, 'narration_box'),
  }
}

function mediaUrl(path: string) {
  return path.startsWith('/') ? `${API_BASE}${path}` : path
}

export const multimodalApi = {
  getScreenplay: (chapterId: string): Promise<Asset> =>
    request<Asset>(`/v1/chapters/${encodeURIComponent(chapterId)}/screenplay`),

  regenerateScreenplay: (chapterId: string): Promise<RegenerateJobResponse> =>
    request<RegenerateJobResponse>(`/v1/chapters/${encodeURIComponent(chapterId)}/screenplay`, { method: 'POST' }),

  regenerateStoryboard: (chapterId: string): Promise<RegenerateJobResponse> =>
    request<RegenerateJobResponse>(`/v1/chapters/${encodeURIComponent(chapterId)}/storyboard`, { method: 'POST' }),

  regenerateVideo: (chapterId: string): Promise<RegenerateJobResponse> =>
    request<RegenerateJobResponse>(`/v1/chapters/${encodeURIComponent(chapterId)}/video`, { method: 'POST' }),

  getVideoStationStatus: (chapterId: string): Promise<VideoStationStatus> =>
    request<VideoStationStatus>(`/v1/chapters/${encodeURIComponent(chapterId)}/video/status`),

  composeVideo: (chapterId: string, body?: ComposeVideoRequest): Promise<RegenerateJobResponse> =>
    request<RegenerateJobResponse>(`/v1/chapters/${encodeURIComponent(chapterId)}/compose`, {
      method: 'POST',
      body,
    }),

  listBGMTracks: async (projectId: string): Promise<BGMTrack[]> => {
    const data = await request<BGMTracksResponse>(`/v1/chapters/compose/bgm?project_id=${encodeURIComponent(projectId)}`)
    return data.items ?? []
  },

  getComposeJob: (chapterId: string, jobId: string): Promise<ComposeJobView> =>
    request<ComposeJobView>(
      `/v1/chapters/${encodeURIComponent(chapterId)}/compose/jobs/${encodeURIComponent(jobId)}`,
    ),

  regenerateAudio: (chapterId: string, voiceOverride?: AudioVoiceOverride): Promise<RegenerateJobResponse> =>
    request<RegenerateJobResponse>(`/v1/chapters/${encodeURIComponent(chapterId)}/audio`, {
      method: 'POST',
      body: hasAudioVoiceOverride(voiceOverride) ? voiceOverride : undefined,
    }),

  listStoryboardPanels: async (storyboardAssetId: string): Promise<StoryboardPanel[]> => {
    const data = await request<StoryboardPanelsResponse>(`/v1/storyboards/${encodeURIComponent(storyboardAssetId)}/panels`)
    return sortStoryboardPanels(data.panels ?? [])
  },

  getStoryboardSketch: async (storyboardAssetId: string): Promise<SketchFrame[]> => {
    const data = await request<SketchFramesResponse>(`/v1/storyboards/${encodeURIComponent(storyboardAssetId)}/sketch`)
    return data.frames ?? []
  },

  updateStoryboardSketch: (storyboardAssetId: string, panelId: string, body: UpdateSketchFrameRequest): Promise<SketchFrameResponse> =>
    request<SketchFrameResponse>(
      `/v1/storyboards/${encodeURIComponent(storyboardAssetId)}/sketch/${encodeURIComponent(panelId)}`,
      { method: 'PUT', body },
    ),

  renderStoryboardSketch: (storyboardAssetId: string, panelId: string): Promise<SketchFrameResponse> =>
    request<SketchFrameResponse>(
      `/v1/storyboards/${encodeURIComponent(storyboardAssetId)}/sketch/${encodeURIComponent(panelId)}/render`,
      { method: 'POST' },
    ),

  confirmStoryboardSketch: (storyboardAssetId: string): Promise<SketchFramesResponse> =>
    request<SketchFramesResponse>(`/v1/storyboards/${encodeURIComponent(storyboardAssetId)}/sketch/confirm`, { method: 'POST' }),

  replayPanelCandidate: (panelId: string, assetId: string): Promise<RegenerateJobResponse> =>
    request<RegenerateJobResponse>(`/v1/panels/${encodeURIComponent(panelId)}/pool-assets/${encodeURIComponent(assetId)}/replay`, { method: 'POST' }),

  renderPanel: (panelId: string, body?: PanelImageRenderRequest): Promise<RegenerateJobResponse> =>
    request<RegenerateJobResponse>(`/v1/panels/${encodeURIComponent(panelId)}/render`, {
      method: 'POST',
      body: body && Object.keys(body).length > 0 ? body : undefined,
    }),

  selectPanelPoolAsset: (panelId: string, assetId: string): Promise<Asset> =>
    request<Asset>(
      `/v1/panels/${encodeURIComponent(panelId)}/pool-assets/${encodeURIComponent(assetId)}/select`,
      { method: 'POST' },
    ),

  getAudio: (chapterId: string): Promise<Asset> => request<Asset>(`/v1/chapters/${encodeURIComponent(chapterId)}/audio`),

  regenerateCharacterDesign: (projectId: string, characterId: string): Promise<RegenerateJobResponse> =>
    request<RegenerateJobResponse>(
      `/v1/projects/${encodeURIComponent(projectId)}/characters/${encodeURIComponent(characterId)}/design`,
      { method: 'POST' },
    ),

  panelImageUrl: (panelId: string): string => mediaUrl(`/v1/panels/${encodeURIComponent(panelId)}/image`),
  sketchThumbnailUrl: (panelId: string): string => mediaUrl(`/v1/panels/${encodeURIComponent(panelId)}/sketch-image`),

  panelPoolAssetImageUrl: (panelId: string, assetId: string): string =>
    mediaUrl(`/v1/panels/${encodeURIComponent(panelId)}/pool-assets/${encodeURIComponent(assetId)}/image`),

  chapterImageRawUrl: (chapterId: string, imageId: string): string =>
    mediaUrl(`/v1/chapters/${encodeURIComponent(chapterId)}/images/${encodeURIComponent(imageId)}/raw`),

  characterDesignImageUrl: (projectId: string, characterId: string): string =>
    mediaUrl(`/v1/projects/${encodeURIComponent(projectId)}/characters/${encodeURIComponent(characterId)}/design/image`),

  audioRawUrl: (chapterId: string): string => mediaUrl(`/v1/chapters/${encodeURIComponent(chapterId)}/audio/raw`),

  videoRawUrl: (chapterId: string): string => mediaUrl(`/v1/chapters/${encodeURIComponent(chapterId)}/video/raw`),
}

function hasAudioVoiceOverride(value: AudioVoiceOverride | undefined): value is AudioVoiceOverride {
  if (!value) return false
  if (value.narrator_voice?.trim()) return true
  if (value.speech_rate !== undefined && value.speech_rate > 0) return true
  return Boolean(value.voice_map && Object.keys(value.voice_map).length > 0)
}
